<template lang="pug">
.pulse-config
  .config-label {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.title') }}
  .total-metrics-section
    .section-header
      .title-with-info
        h3 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.all_segments_metrics') }}
      .metric-count {{ localConfig.total_metrics.length }} / {{ maxMetrics }}
    .metrics-tags
      aiq-tag(
        v-for="metricId in localConfig.total_metrics"
        :key="metricId"
        closable
        type="info"
        @close="onRemoveTotalMetric(metricId)"
      ) {{ getMetricLabel(metricId) }}
      .add-metric-dropdown(v-if="!isTotalMetricsMaxed")
        aiq-select(
          :placeholder="$t('settings_tab.agent_tab.dashboard.pulse_configuration.add_metric')"
          clearable
          :filterable="selectConfig.filterable"
          :empty-values="selectConfig.emptyValues"
          :value-on-clear="''"
          @change="onAddTotalMetric"
          popper-class="pulse-config-dropdown"
        )
          aiq-option(
            v-for="metric in availableMetricsForTotal"
            :key="metric.id"
            :label="metric.label"
            :value="metric.id"
          )
      .max-reached(v-else) {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.selection_limit_reached') }}

  PulseConfigSegment(
    v-for="(segment, index) in localConfig.segments"
    :key="index"
    :segment="segment"
    :index="index"
    :availableSegments="availableSegmentsByIndex[index]"
    :availableMetrics="availableMetrics"
    :maxMetrics="maxMetrics"
    @update="onSegmentUpdate"
  )

  .cost-config-section
    .section-header
      .title-with-info
        h3 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.cost_config.title') }}
    p.cost-description {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.cost_config.description') }}
    .cost-fields
      .cost-field
        span.cost-field-label {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.cost_config.phone_call_cost') }}
        .cost-input-row
          span.currency-symbol $
          aiq-input-number(
            v-model="localConfig.cost_config.phone_call_cost"
            size="small"
            :min="0"
            :precision="2"
            :controls="false"
          )
      .cost-field
        span.cost-field-label {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.cost_config.agent_chat_cost') }}
        .cost-input-row
          span.currency-symbol $
          aiq-input-number(
            v-model="localConfig.cost_config.agent_chat_cost"
            size="small"
            :min="0"
            :precision="2"
            :controls="false"
          )
      .cost-field
        span.cost-field-label {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.cost_config.automated_conversation_cost') }}
        .cost-input-row
          span.currency-symbol $
          aiq-input-number(
            v-model="localConfig.cost_config.automated_conversation_cost"
            size="small"
            :min="0"
            :precision="2"
            :controls="false"
          )

  .sales-lead-tags-section
    .section-header
      .title-with-info
        h3 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.sales_lead_tags.title') }}
    p.cost-description {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.sales_lead_tags.description') }}
    aiq-select(
      v-model="localConfig.sales_lead_tags"
      multiple
      filterable
      :placeholder="$t('settings_tab.agent_tab.dashboard.pulse_configuration.sales_lead_tags.placeholder')"
      popper-class="pulse-config-dropdown"
    )
      aiq-option(
        v-for="tagName in availableTags"
        :key="tagName"
        :label="tagName"
        :value="tagName"
      )

  .action-buttons
    aiq-button(@click="onCancel") {{ $t('global.cancel') }}
    aiq-button(@click="onSave" :disabled="!isDataChanged" type="primary") {{ $t('global.save') }}
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import without from 'lodash/without';
import difference from 'lodash/difference';
import PulseConfigSegment from './components/PulseConfigSegment.vue';
import { DEFAULT_COST_CONFIG } from '@/pages/Pulse/365/constants';

const METRIC_IDS = [
  'active_unique_customers',
  'conversations',
  'messages',
  'first_response_time',
  'agent_first_response_time',
  'handle_time',
  'median_handle_time',
  'fully_automated_conversations',
  'partially_automated_conversations',
  'feedback_score',
  'automation_driven_cost_savings',
  'channel_cost_savings',
  'relationship_length',
  'sales_leads',
];

const DEFAULT_CONFIG = {
  total_metrics: [],
  segments: [
    { segment_names: [], metrics: [] },
    { segment_names: [], metrics: [] },
  ],
  cost_config: { ...DEFAULT_COST_CONFIG },
  sales_lead_tags: [],
};

function mergeWithDefaults(config) {
  const savedCostConfig = get(config, 'cost_config', {});
  const costConfig = { ...DEFAULT_CONFIG.cost_config };

  Object.keys(costConfig).forEach(key => {
    if (!isNil(savedCostConfig[key])) {
      costConfig[key] = savedCostConfig[key];
    }
  });

  return {
    ...DEFAULT_CONFIG,
    ...config,
    cost_config: costConfig,
    sales_lead_tags: [...(config?.sales_lead_tags || [])],
  };
}

export default {
  name: 'PulseConfig',
  components: {
    PulseConfigSegment,
  },
  props: {
    config: {
      type: Object,
      default: () => cloneDeep(DEFAULT_CONFIG),
    },
    segments: {
      type: Array,
      default: () => [],
    },
    availableTags: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['save'],
  data() {
    return {
      selectConfig: {
        filterable: true,
        emptyValues: [''],
      },
      localConfig: cloneDeep(mergeWithDefaults(this.config)),
      maxMetrics: 5,
    };
  },
  computed: {
    baseConfig() {
      return mergeWithDefaults(this.config);
    },
    availableMetrics() {
      return METRIC_IDS.map(id => ({
        id,
        label: this.$t(`settings_tab.agent_tab.dashboard.pulse_configuration.metrics.${id}`),
      }));
    },
    isDataChanged() {
      return !isEqual(this.localConfig, this.baseConfig);
    },
    availableMetricsForTotal() {
      return this.availableMetrics.filter(
        metric => !this.localConfig.total_metrics.includes(metric.id),
      );
    },
    isTotalMetricsMaxed() {
      return this.localConfig.total_metrics.length >= this.maxMetrics;
    },
    availableSegmentsByIndex() {
      return this.localConfig.segments.map((segment, index) => {
        const otherIndex = index === 0 ? 1 : 0;
        const otherSelected = this.localConfig.segments[otherIndex]?.segment_names || [];
        const currentSelected = segment.segment_names || [];
        return difference(this.segments, difference(otherSelected, currentSelected));
      });
    },
  },
  watch: {
    baseConfig: {
      handler(newVal) {
        this.localConfig = cloneDeep(newVal);
      },
      deep: true,
    },
  },
  methods: {
    getMetricLabel(metricId) {
      const metric = this.availableMetrics.find(m => m.id === metricId);
      return metric ? metric.label : metricId;
    },

    onAddTotalMetric(metricId) {
      if (metricId && !this.localConfig.total_metrics.includes(metricId) && !this.isTotalMetricsMaxed) {
        this.localConfig.total_metrics.push(metricId);
      }
    },

    onRemoveTotalMetric(metricId) {
      this.localConfig.total_metrics = without(this.localConfig.total_metrics, metricId);
    },

    onSegmentUpdate(index, updatedSegment) {
      this.localConfig.segments[index] = { ...updatedSegment };
    },

    onSave() {
      const cleanedSegments = (this.localConfig.segments || DEFAULT_CONFIG.segments).map(segment => ({
        segment_names: segment.segment_names || [],
        metrics: segment.metrics || [],
      }));

      const normalizeCost = (val) => (typeof val === 'number' && Number.isFinite(val) ? val : null);

      const cleanConfig = {
        total_metrics: this.localConfig.total_metrics || DEFAULT_CONFIG.total_metrics,
        segments: cleanedSegments,
        cost_config: {
          phone_call_cost: normalizeCost(this.localConfig.cost_config?.phone_call_cost),
          agent_chat_cost: normalizeCost(this.localConfig.cost_config?.agent_chat_cost),
          automated_conversation_cost: normalizeCost(this.localConfig.cost_config?.automated_conversation_cost),
        },
        sales_lead_tags: this.localConfig.sales_lead_tags || [],
      };
      this.$emit('save', cleanConfig);
    },

    onCancel() {
      this.localConfig = cloneDeep(this.baseConfig);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../styles/aiq-variables.scss";

.pulse-config {
  padding: 15px;
  background-color: $aiq-bgc-message-input;
  border-radius: 4px;
}

.config-label {
  font-size: 12px;
  font-weight: 600;
  color: $aiq-color-text-muted;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 15px;
}

.total-metrics-section {
  width: 100%;
  padding-bottom: 20px;
  border-bottom: 1px solid $aiq-bdc-input-default;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.title-with-info {
  display: flex;
  align-items: center;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: $aiq-color-text-primary;
  }
}

.metric-count {
  font-size: 13px;
  color: $aiq-color-text-muted;
  font-weight: 500;
}

.metrics-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;
  min-height: 32px;

  ::v-deep .aiq-tag {
    margin: 0;
    background-color: $aiq-color-active;
    border-color: $aiq-color-active;
    color: white;
    height: 38px;
    padding: 0 18px;
    border-radius: 19px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    line-height: normal;
    font-weight: 500;

    &:hover {
      background-color: darken($aiq-color-active, 10%);
      border-color: darken($aiq-color-active, 10%);
    }

    .el-tag__close {
      color: white;
      margin-left: 8px;
      margin-top: 0;
      display: flex;
      align-items: center;
    }
  }
}

.add-metric-dropdown {
  display: inline-block;
  min-width: 180px;
  position: relative;

  ::v-deep {
    .el-input--suffix .el-input__inner {
      padding-right: 30px;
    }
  }
}

.max-reached {
  color: $aiq-color-text-muted;
  font-size: 13px;
  padding: 8px 0;
  font-weight: 500;
}

.cost-config-section {
  padding-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid $aiq-bdc-input-default;
}

.sales-lead-tags-section {
  padding-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid $aiq-bdc-input-default;
}

.cost-description {
  margin: 4px 0 16px;
  font-size: 13px;
  color: $aiq-color-text-muted;
  line-height: 1.5;
}

.cost-fields {
  display: flex;
  gap: 32px;
}

.cost-field {
  display: flex;
  flex-direction: column;
  gap: 8px;

  :deep(.el-input) {
    width: 120px;
  }

  :deep(.el-input-number .el-input__inner) {
    text-align: left;
  }
}

.cost-field-label {
  font-size: 13px;
  font-weight: 500;
  color: $aiq-color-text-primary;
}

.cost-input-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.currency-symbol {
  font-size: 14px;
  font-weight: 500;
  color: $aiq-color-text-primary;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
}
</style>

<style lang="scss">
@import "../../../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .pulse-config {
  background-color: $aiq-bgc-sidebar-item-hover;

  .config-label {
    color: $aiq-color-text-muted;
  }

  .title-with-info h3 {
    color: $aiq-color-black;
  }

  .total-metrics-section {
    border-bottom-color: $aiq-bdc-input-default;
  }

  ::v-deep .el-input__inner {
    color: white;
  }

  .metric-count {
    color: white;
  }

  .max-reached {
    color: white;
  }

  .cost-config-section {
    border-bottom-color: $aiq-bdc-input-default;
  }

  .sales-lead-tags-section {
    border-bottom-color: $aiq-bdc-input-default;
  }

  .cost-field-label {
    color: $aiq-color-black;
  }

  .currency-symbol {
    color: $aiq-color-black;
  }

  .action-buttons {
    border-top-color: $aiq-color-border-dark;
  }
}

.pulse-segment-checkbox-dropdown {
  .el-select-dropdown__item {
    padding: 8px 12px;
    height: auto;
    line-height: normal;

    &.selected::after,
    &.is-selected::after {
      display: none;
      content: none;
    }

    .el-icon {
      display: none;
    }

    .checkbox-option {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;

      .el-checkbox {
        pointer-events: none;
        margin-right: 0;
        height: auto;

        .el-checkbox__input {
          display: flex;
          align-items: center;
        }

        .el-checkbox__label {
          display: none;
        }
      }

      .option-label {
        color: inherit;
        font-size: 14px;
        line-height: 1.4;
      }
    }
  }
}

[data-theme="dark"] {
  .pulse-config-dropdown .el-select-dropdown__item {
    color: white;
  }

  .pulse-segment-checkbox-dropdown .el-select-dropdown__item .checkbox-option .option-label {
    color: white;
  }
}
</style>
